import type {
  MapCategory,
  MapArtwork,
  MapArtworkLabel,
  MapDocument,
  MapGeometry,
  MapLabels,
  MapLocation,
  MapLogo,
  MapLabelMode,
  MapStand,
  MapRouting,
} from "./types";

export const MAP_DOCUMENT_MAX_BYTES = 1_048_576;
export const MAP_LOGO_MAX_DIMENSION = 4_096;
export const MAP_LOGO_MAX_PIXELS = 8_000_000;
export const MAP_ARCHITECTURE_MAX_POLYGONS = 2_000;
export const MAP_ARCHITECTURE_MAX_RINGS = 4_096;
export const MAP_ARCHITECTURE_MAX_RING_VERTICES = 4_096;
export const MAP_ARCHITECTURE_MAX_VERTICES = 40_000;
export const MAP_ARCHITECTURE_MAX_HOLE_COMPARISONS = 20_000_000;
export const MAP_ARTWORK_MAX_ITEMS = 2_000;
export const MAP_ARTWORK_MAX_POLYGONS = 4_000;
export const MAP_ARTWORK_MAX_RINGS = 4_096;
export const MAP_ARTWORK_MAX_RING_VERTICES = 4_096;
export const MAP_ARTWORK_MAX_VERTICES = 40_000;
export const MAP_ARTWORK_MAX_LABELS = 500;
export const MAP_ARTWORK_MAX_EDGE_COMPARISONS = 20_000_000;
export const MAP_ROUTING_MAX_AREAS = 64;
export const MAP_ROUTING_MAX_RINGS = 8;
export const MAP_ROUTING_MAX_RING_VERTICES = 128;
export const MAP_ROUTING_MAX_VERTICES = 4_096;
export const MAP_ROUTING_MAX_BARRIERS = 256;
export const MAP_ROUTING_MAX_DOORS = 256;
export function validLogoId(value: unknown): value is string {
  return typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
}

const MAX_COORDINATE = 100_000;
const MAX_PATH_CHARACTERS = 600_000;
const MAX_VERTICES = 50_000;
const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const CATEGORIES = new Set<MapCategory>([
  "exhibitor",
  "stage",
  "lounge",
  "facility",
  "unassigned",
]);
const PATH_ARITY: Readonly<Record<string, number>> = {
  m: 2,
  l: 2,
  h: 1,
  v: 1,
  c: 6,
  s: 4,
  q: 4,
  t: 2,
  a: 7,
  z: 0,
};

/** An invalid document is never persisted or passed to either map renderer. */
export class MapValidationError extends Error {
  readonly code = "invalid_map";

  constructor(message: string) {
    super(message);
    this.name = "MapValidationError";
  }
}

export function validMapId(id: unknown): id is string {
  return (
    typeof id === "string" &&
    /^[a-z0-9][a-z0-9-]{0,79}$/.test(id) &&
    !FORBIDDEN_KEYS.has(id)
  );
}

function invalid(path: string, reason: string): never {
  throw new MapValidationError(`${path}: ${reason}.`);
}

function record(
  value: unknown,
  path: string,
  keys?: readonly string[],
  optionalKeys: readonly string[] = [],
): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    invalid(path, "must be an object");
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null)
    invalid(path, "must be a plain object");
  const allowed = keys ? new Set([...keys, ...optionalKeys]) : undefined;
  for (const key of Reflect.ownKeys(value)) {
    if (
      typeof key !== "string" ||
      FORBIDDEN_KEYS.has(key) ||
      (allowed && !allowed.has(key))
    ) {
      invalid(path, "contains an unsupported field");
    }
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !("value" in descriptor) || !descriptor.enumerable)
      invalid(path, "contains an unsupported property");
  }
  if (keys?.some((key) => !Object.hasOwn(value, key)))
    invalid(path, "is missing a required field");
  return value as Record<string, unknown>;
}

function array(
  value: unknown,
  path: string,
  maximum: number,
  minimum = 0,
): unknown[] {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype)
    invalid(path, "must be an array");
  if (value.length < minimum || value.length > maximum)
    invalid(path, `must contain between ${minimum} and ${maximum} items`);
  for (const key of Reflect.ownKeys(value)) {
    if (key === "length") continue;
    if (
      typeof key !== "string" ||
      !/^(0|[1-9]\d*)$/.test(key) ||
      Number(key) >= value.length
    )
      invalid(path, "contains an unsupported property");
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !("value" in descriptor) || !descriptor.enumerable)
      invalid(path, "contains an unsupported property");
  }
  for (let index = 0; index < value.length; index++) {
    if (!Object.hasOwn(value, index))
      invalid(path, "must not contain missing items");
  }
  return value;
}

function number(
  value: unknown,
  path: string,
  minimum: number,
  maximum: number,
  positive = false,
): number {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < minimum ||
    value > maximum ||
    (positive && value === 0)
  ) {
    invalid(
      path,
      `must be a finite number ${positive ? "greater than zero and " : ""}between ${minimum} and ${maximum}`,
    );
  }
  return value;
}

function text(
  value: unknown,
  path: string,
  maximum: number,
  required = false,
  multiline = false,
): string {
  const controls = multiline
    ? /[\u0000-\u0009\u000b\u000c\u000e-\u001f\u007f-\u009f]/
    : /[\u0000-\u001f\u007f-\u009f]/;
  if (
    typeof value !== "string" ||
    value.length > maximum ||
    controls.test(value) ||
    (required && !value.trim())
  ) {
    invalid(
      path,
      `must be ${required ? "non-empty " : ""}text of at most ${maximum} characters without control characters`,
    );
  }
  return value;
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") invalid(path, "must be a boolean");
  return value;
}

function logo(value: unknown, path: string): MapLogo {
  const input = record(value, path, ["id", "width", "height"]);
  if (!validLogoId(input.id))
    invalid(`${path}.id`, "must be an uploaded logo identifier");
  const width = number(input.width, `${path}.width`, 1, MAP_LOGO_MAX_DIMENSION);
  const height = number(
    input.height,
    `${path}.height`,
    1,
    MAP_LOGO_MAX_DIMENSION,
  );
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width * height > MAP_LOGO_MAX_PIXELS
  )
    invalid(
      path,
      "must have integer dimensions and no more than 8 million pixels",
    );
  return { id: input.id, width, height };
}
function labelMode(value: unknown, path: string): MapLabelMode {
  if (value !== "text" && value !== "logo" && value !== "logo-text")
    invalid(path, "must be text, logo or logo-text");
  return value;
}

function labelRotation(value: unknown, path: string): -90 | 0 | 90 {
  if (value !== -90 && value !== 0 && value !== 90)
    invalid(path, "must be -90, 0 or 90 degrees");
  return value;
}

function entityId(value: unknown, path: string): string {
  const id = text(value, path, 120, true);
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]*$/.test(id) || FORBIDDEN_KEYS.has(id))
    invalid(path, "must be a safe location identifier");
  return id;
}

function uniqueId(value: unknown, path: string, used: Set<string>): string {
  const id = entityId(value, path);
  if (used.has(id)) invalid(path, "must be unique");
  used.add(id);
  return id;
}

/** Checks SVG path grammar without accepting markup, style, URLs, or non-finite operands. */
function validatePath(value: unknown, path: string): string {
  if (typeof value !== "string" || value.length > MAX_PATH_CHARACTERS)
    invalid(path, "must be a bounded SVG path string");
  if (!value.trim()) invalid(path, "must contain SVG path commands");
  const token =
    /[MmLlHhVvCcSsQqTtAaZz]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/y;
  let position = 0;
  let command = "";
  let operands = 0;
  let hasCommand = false;
  const finishCommand = () => {
    if (
      command &&
      (operands < PATH_ARITY[command]! ||
        operands % (PATH_ARITY[command] || 1) !== 0)
    )
      invalid(path, "contains incomplete SVG path coordinates");
  };
  while (position < value.length) {
    const character = value[position]!;
    if (/[ \t\r\n,]/.test(character)) {
      position++;
      continue;
    }
    token.lastIndex = position;
    const match = token.exec(value);
    if (!match) invalid(path, "contains invalid SVG path syntax");
    const part = match[0];
    position = token.lastIndex;
    if (/^[A-Za-z]$/.test(part)) {
      finishCommand();
      command = part.toLowerCase();
      if (!hasCommand && command !== "m")
        invalid(path, "must begin with an SVG move command");
      hasCommand = true;
      operands = 0;
      continue;
    }
    if (!command || command === "z")
      invalid(path, "contains coordinates without an SVG command");
    const operand = Number(part);
    if (!Number.isFinite(operand) || Math.abs(operand) > MAX_COORDINATE)
      invalid(path, "contains out-of-range SVG coordinates");
    if (command === "a") {
      const slot = operands % 7;
      if ((slot === 0 || slot === 1) && operand < 0)
        invalid(path, "contains a negative arc radius");
      if ((slot === 3 || slot === 4) && part !== "0" && part !== "1")
        invalid(path, "contains an invalid arc flag");
    }
    operands++;
  }
  finishCommand();
  return value;
}

function labels(
  value: unknown,
  path: string,
  standIds: Set<string>,
  entranceIds: Set<string>,
): MapLabels {
  const source = record(value, path, ["stands", "entrances"]);
  let totalCharacters = 0;
  const paths = (
    input: unknown,
    kind: "stands" | "entrances",
    ids: Set<string>,
  ): Record<string, string> => {
    const entries = record(input, `${path}.${kind}`);
    const result: Record<string, string> = {};
    for (const [id, value] of Object.entries(entries)) {
      if (!ids.has(id))
        invalid(
          `${path}.${kind}`,
          "contains an identifier missing from the geometry",
        );
      if (typeof value !== "string")
        invalid(`${path}.${kind}.${id}`, "must be an SVG path string");
      totalCharacters += value.length;
      if (totalCharacters > MAX_PATH_CHARACTERS)
        invalid(
          path,
          `must not exceed ${MAX_PATH_CHARACTERS} path characters in total`,
        );
      result[id] = validatePath(value, `${path}.${kind}.${id}`);
    }
    return result;
  };
  return {
    stands: paths(source.stands, "stands", standIds),
    entrances: paths(source.entrances, "entrances", entranceIds),
  };
}

function mapColor(value: unknown, path: string): string {
  if (typeof value !== "string" || !/^#[a-fA-F0-9]{6}$/.test(value))
    invalid(path, "must be a six-digit hex colour such as #1b2646");
  return value.toLowerCase();
}

function architectureBounds(ring: number[][]) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    minX = Math.min(minX, x!);
    minY = Math.min(minY, y!);
    maxX = Math.max(maxX, x!);
    maxY = Math.max(maxY, y!);
  }
  return { minX, minY, maxX, maxY };
}

function validateArchitectureHoles(
  polygon: number[][][],
  path: string,
  budget: { comparisons: number },
  budgetPath = "map.geometry.architecture",
) {
  if (polygon.length < 2) return;
  const outer = polygon[0]!;
  const bounds = architectureBounds(outer);
  const epsilon = 1e-8;
  for (let ringIndex = 1; ringIndex < polygon.length; ringIndex++) {
    const hole = polygon[ringIndex]!;
    const ringPath = `${path}[${ringIndex}]`;
    const holeBounds = architectureBounds(hole);
    if (
      holeBounds.minX < bounds.minX - epsilon ||
      holeBounds.maxX > bounds.maxX + epsilon ||
      holeBounds.minY < bounds.minY - epsilon ||
      holeBounds.maxY > bounds.maxY + epsilon
    )
      invalid(ringPath, "hole must stay inside its outer ring");
    for (let pointIndex = 0; pointIndex < hole.length; pointIndex++) {
      const point = hole[pointIndex]!;
      const next = hole[(pointIndex + 1) % hole.length]!;
      const x = point[0]!;
      const y = point[1]!;
      const nextX = next[0]!;
      const nextY = next[1]!;
      const middleX = (x + nextX) / 2;
      const middleY = (y + nextY) / 2;
      const holeDx = nextX - x;
      const holeDy = nextY - y;
      const minY = Math.min(y, nextY) - epsilon;
      const maxY = Math.max(y, nextY) + epsilon;
      const minX = Math.min(x, nextX) - epsilon;
      const holeEpsilon =
        epsilon * Math.max(1, Math.abs(holeDx) + Math.abs(holeDy));
      let inside = false;
      let onBoundary = false;
      let middleInside = false;
      let middleOnBoundary = false;
      for (let index = 0; index < outer.length; index++) {
        // Check the vertex, midpoint and intersection together for each edge
        // pair, charging the shared budget once for this bounded batch of work.
        budget.comparisons++;
        if (budget.comparisons > MAP_ARCHITECTURE_MAX_HOLE_COMPARISONS)
          invalid(
            budgetPath,
            `must not exceed ${MAP_ARCHITECTURE_MAX_HOLE_COMPARISONS} hole-edge comparisons`,
          );
        const a = outer[index]!;
        const b = outer[(index + 1) % outer.length]!;
        // Neither the segment nor the two rightward containment rays can
        // meet an edge entirely above, below or to the left of this hole edge.
        if (
          Math.max(a[1]!, b[1]!) < minY ||
          Math.min(a[1]!, b[1]!) > maxY ||
          Math.max(a[0]!, b[0]!) < minX
        )
          continue;
        const dx = b[0]! - a[0]!;
        const dy = b[1]! - a[1]!;
        const edgeEpsilon = epsilon * Math.max(1, Math.abs(dx) + Math.abs(dy));
        const pointCross = dx * (y - a[1]!) - dy * (x - a[0]!);
        const nextCross = dx * (nextY - a[1]!) - dy * (nextX - a[0]!);
        // A strict crossing cannot belong to a contained hole. Collinear
        // segments and endpoint touches remain allowed.
        if (
          (pointCross > edgeEpsilon && nextCross < -edgeEpsilon) ||
          (pointCross < -edgeEpsilon && nextCross > edgeEpsilon)
        ) {
          const aCross = holeDx * (a[1]! - y) - holeDy * (a[0]! - x);
          const bCross = holeDx * (b[1]! - y) - holeDy * (b[0]! - x);
          if (
            (aCross > holeEpsilon && bCross < -holeEpsilon) ||
            (aCross < -holeEpsilon && bCross > holeEpsilon)
          )
            invalid(ringPath, "hole must stay inside its outer ring");
        }
        // Traced rings may touch their outer ring at a vertex or edge. Permit
        // these boundary points without depending on winding or ray direction.
        if (
          x >= Math.min(a[0]!, b[0]!) - epsilon &&
          x <= Math.max(a[0]!, b[0]!) + epsilon &&
          y >= Math.min(a[1]!, b[1]!) - epsilon &&
          y <= Math.max(a[1]!, b[1]!) + epsilon &&
          Math.abs(pointCross) <= edgeEpsilon
        )
          onBoundary = true;
        if (a[1]! > y !== b[1]! > y && x < a[0]! + (dx * (y - a[1]!)) / dy)
          inside = !inside;
        // A bridge between two boundary vertices may leave a concavity
        // without a strict edge crossing; check the midpoint as well.
        if (
          middleX >= Math.min(a[0]!, b[0]!) - epsilon &&
          middleX <= Math.max(a[0]!, b[0]!) + epsilon &&
          middleY >= Math.min(a[1]!, b[1]!) - epsilon &&
          middleY <= Math.max(a[1]!, b[1]!) + epsilon &&
          Math.abs((pointCross + nextCross) / 2) <= edgeEpsilon
        )
          middleOnBoundary = true;
        if (
          a[1]! > middleY !== b[1]! > middleY &&
          middleX < a[0]! + (dx * (middleY - a[1]!)) / dy
        )
          middleInside = !middleInside;
      }
      if ((!inside && !onBoundary) || (!middleInside && !middleOnBoundary))
        invalid(ringPath, "hole must stay inside its outer ring");
    }
  }
}

/** Coordinates only: architectural strokes never contain markup or source metadata. */
function architecture(
  value: unknown,
  floor: MapGeometry["floor"],
  holeBudget = { comparisons: 0 },
): NonNullable<MapGeometry["architecture"]> {
  const path = "map.geometry.architecture";
  const tolerance = 0.002;
  let rings = 0;
  let vertices = 0;
  return array(value, path, MAP_ARCHITECTURE_MAX_POLYGONS).map(
    (value, polygonIndex) => {
      const polygonPath = `${path}[${polygonIndex}]`;
      const polygon = array(
        value,
        polygonPath,
        MAP_ARCHITECTURE_MAX_RINGS,
        1,
      ).map((value, ringIndex) => {
        if (++rings > MAP_ARCHITECTURE_MAX_RINGS)
          invalid(
            path,
            `must not exceed ${MAP_ARCHITECTURE_MAX_RINGS} rings in total`,
          );
        const ringPath = `${polygonPath}[${ringIndex}]`;
        const ring = array(
          value,
          ringPath,
          MAP_ARCHITECTURE_MAX_RING_VERTICES,
          3,
        ).map((value, pointIndex) => {
          if (++vertices > MAP_ARCHITECTURE_MAX_VERTICES)
            invalid(
              path,
              `must not exceed ${MAP_ARCHITECTURE_MAX_VERTICES} vertices in total`,
            );
          const pointPath = `${ringPath}[${pointIndex}]`;
          const point = array(value, pointPath, 2, 2);
          return [
            number(
              point[0],
              `${pointPath}[0]`,
              floor.x - tolerance,
              floor.x + floor.width + tolerance,
            ),
            number(
              point[1],
              `${pointPath}[1]`,
              floor.y - tolerance,
              floor.y + floor.height + tolerance,
            ),
          ];
        });
        // The translated sum avoids cancellation for small strokes at large coordinates.
        const origin = ring[0]!;
        let twiceArea = 0;
        for (let index = 0; index < ring.length; index++) {
          const a = ring[index]!;
          const b = ring[(index + 1) % ring.length]!;
          twiceArea +=
            (a[0]! - origin[0]!) * (b[1]! - origin[1]!) -
            (b[0]! - origin[0]!) * (a[1]! - origin[1]!);
        }
        if (Math.abs(twiceArea) <= 1e-8)
          invalid(ringPath, "must have nonzero area");
        return ring;
      });
      validateArchitectureHoles(polygon, polygonPath, holeBudget);
      return polygon;
    },
  );
}

/** Source paint is data only; markup, CSS functions and private metadata are rejected. */
function artwork(
  value: unknown,
  floor: MapGeometry["floor"],
  budget: { comparisons: number },
): MapArtwork[] {
  const path = "map.geometry.artwork";
  let polygonCount = 0,
    ringCount = 0,
    vertices = 0;
  const charge = (count = 1) => {
    budget.comparisons += count;
    if (budget.comparisons > MAP_ARTWORK_MAX_EDGE_COMPARISONS)
      invalid(
        path,
        `must not exceed ${MAP_ARTWORK_MAX_EDGE_COMPARISONS} edge comparisons`,
      );
  };
  const ring = (value: unknown, ringPath: string): number[][] => {
    if (++ringCount > MAP_ARTWORK_MAX_RINGS)
      invalid(path, `must not exceed ${MAP_ARTWORK_MAX_RINGS} rings in total`);
    const points = array(value, ringPath, MAP_ARTWORK_MAX_RING_VERTICES, 3).map(
      (value, i) => {
        if (++vertices > MAP_ARTWORK_MAX_VERTICES)
          invalid(
            path,
            `must not exceed ${MAP_ARTWORK_MAX_VERTICES} vertices in total`,
          );
        const pointPath = `${ringPath}[${i}]`,
          pair = array(value, pointPath, 2, 2);
        return [
          number(
            pair[0],
            `${pointPath}[0]`,
            floor.x - 0.002,
            floor.x + floor.width + 0.002,
          ),
          number(
            pair[1],
            `${pointPath}[1]`,
            floor.y - 0.002,
            floor.y + floor.height + 0.002,
          ),
        ];
      },
    );
    // Authors may repeat the closing vertex; storage uses implicit closure.
    if (
      points.length > 3 &&
      points[0]![0] === points.at(-1)![0] &&
      points[0]![1] === points.at(-1)![1]
    )
      points.pop();
    const origin = points[0]!;
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const a = points[i]!,
        b = points[(i + 1) % points.length]!;
      if (a[0] === b[0] && a[1] === b[1])
        invalid(ringPath, "must not contain duplicate consecutive vertices");
      area +=
        (a[0]! - origin[0]!) * (b[1]! - origin[1]!) -
        (b[0]! - origin[0]!) * (a[1]! - origin[1]!);
      for (let j = i + 2; j < points.length; j++) {
        if (i === 0 && j === points.length - 1) continue;
        charge();
        if (
          routingSegmentsMeet(
            a,
            b,
            points[j]!,
            points[(j + 1) % points.length]!,
          )
        )
          invalid(ringPath, "must be a simple ring without self-intersections");
      }
    }
    if (Math.abs(area) <= 1e-8) invalid(ringPath, "must have nonzero area");
    return points;
  };
  return array(value, path, MAP_ARTWORK_MAX_ITEMS).map((value, index) => {
    const itemPath = `${path}[${index}]`;
    const input = record(value, itemPath, ["polygons", "color"], ["opacity"]);
    const opacity = Object.hasOwn(input, "opacity")
      ? number(input.opacity, `${itemPath}.opacity`, 0, 1)
      : undefined;
    const polygons = array(
      input.polygons,
      `${itemPath}.polygons`,
      MAP_ARTWORK_MAX_POLYGONS,
      1,
    ).map((value, index) => {
      if (++polygonCount > MAP_ARTWORK_MAX_POLYGONS)
        invalid(
          path,
          `must not exceed ${MAP_ARTWORK_MAX_POLYGONS} polygons in total`,
        );
      const polygonPath = `${itemPath}.polygons[${index}]`;
      const polygon = array(value, polygonPath, MAP_ARTWORK_MAX_RINGS, 1).map(
        (value, index) => ring(value, `${polygonPath}[${index}]`),
      );
      validateArchitectureHoles(polygon, polygonPath, budget, path);
      // Nested/overlapping holes do not have a portable earcut/nonzero meaning.
      for (let i = 1; i < polygon.length; i++)
        for (let j = i + 1; j < polygon.length; j++) {
          const a = polygon[i]!,
            b = polygon[j]!;
          for (let ai = 0; ai < a.length; ai++)
            for (let bi = 0; bi < b.length; bi++) {
              charge();
              if (
                routingSegmentsMeet(
                  a[ai]!,
                  a[(ai + 1) % a.length]!,
                  b[bi]!,
                  b[(bi + 1) % b.length]!,
                )
              )
                invalid(polygonPath, "hole rings must not touch or overlap");
            }
          if (routingInsideRing(a[0]!, b) || routingInsideRing(b[0]!, a))
            invalid(polygonPath, "hole rings must not be nested");
        }
      return polygon;
    });
    if (opacity !== undefined && opacity < 1 && polygons.length > 1) {
      // SVG paints each source item once; GPU triangles blend individually.
      // Avoid divergent alpha where two polygons in the same paint overlap.
      const bounds = polygons.map((polygon) => architectureBounds(polygon[0]!));
      const insidePaint = (point: number[], polygon: number[][][]): boolean => {
        charge(polygon[0]!.length);
        if (!routingInsideRing(point, polygon[0]!)) return false;
        for (const hole of polygon.slice(1)) {
          charge(hole.length);
          if (routingInsideRing(point, hole)) return false;
        }
        return true;
      };
      for (let i = 0; i < polygons.length; i++) {
        const a = polygons[i]!,
          boundsA = bounds[i]!;
        for (let j = i + 1; j < polygons.length; j++) {
          charge();
          const b = polygons[j]!,
            boundsB = bounds[j]!;
          // A shared bounding-box edge alone has no overlapping filled area.
          if (
            boundsA.maxX <= boundsB.minX ||
            boundsB.maxX <= boundsA.minX ||
            boundsA.maxY <= boundsB.minY ||
            boundsB.maxY <= boundsA.minY
          )
            continue;
          for (const ringA of a)
            for (const ringB of b) {
              for (let ai = 0; ai < ringA.length; ai++)
                for (let bi = 0; bi < ringB.length; bi++) {
                  charge();
                  if (
                    routingSegmentsMeet(
                      ringA[ai]!,
                      ringA[(ai + 1) % ringA.length]!,
                      ringB[bi]!,
                      ringB[(bi + 1) % ringB.length]!,
                    )
                  )
                    invalid(
                      `${itemPath}.polygons`,
                      "translucent polygon boundaries must not cross or touch within overlapping bounds",
                    );
                }
            }
          // Boundary-free containment is overlap only in painted regions;
          // independent islands completely inside a hole remain valid.
          if (insidePaint(a[0]![0]!, b) || insidePaint(b[0]![0]!, a))
            invalid(
              `${itemPath}.polygons`,
              "translucent polygons must not overlap in filled areas",
            );
        }
      }
    }
    return {
      polygons,
      color: mapColor(input.color, `${itemPath}.color`),
      ...(opacity !== undefined ? { opacity } : {}),
    };
  });
}

function artworkLabels(
  value: unknown,
  floor: MapGeometry["floor"],
): MapArtworkLabel[] {
  const path = "map.geometry.artworkLabels";
  return array(value, path, MAP_ARTWORK_MAX_LABELS).map((value, index) => {
    const itemPath = `${path}[${index}]`;
    const input = record(
      value,
      itemPath,
      ["text", "x", "y", "fontSize", "color"],
      ["rotation"],
    );
    return {
      text: text(input.text, `${itemPath}.text`, 300, true),
      x: number(input.x, `${itemPath}.x`, floor.x, floor.x + floor.width),
      y: number(input.y, `${itemPath}.y`, floor.y, floor.y + floor.height),
      fontSize: number(input.fontSize, `${itemPath}.fontSize`, 0.1, 100),
      color: mapColor(input.color, `${itemPath}.color`),
      ...(Object.hasOwn(input, "rotation")
        ? {
            rotation: number(input.rotation, `${itemPath}.rotation`, -360, 360),
          }
        : {}),
    };
  });
}

type RoutingPoint = readonly number[];
const ROUTING_EPSILON = 1e-8;

function routingCross(a: RoutingPoint, b: RoutingPoint, c: RoutingPoint) {
  return (b[0]! - a[0]!) * (c[1]! - a[1]!) - (b[1]! - a[1]!) * (c[0]! - a[0]!);
}

function routingOrientation(a: RoutingPoint, b: RoutingPoint, c: RoutingPoint) {
  const cross = routingCross(a, b, c);
  const tolerance =
    ROUTING_EPSILON * Math.max(1, Math.hypot(b[0]! - a[0]!, b[1]! - a[1]!));
  return cross > tolerance ? 1 : cross < -tolerance ? -1 : 0;
}

function routingOnSegment(
  point: RoutingPoint,
  a: RoutingPoint,
  b: RoutingPoint,
) {
  return (
    routingOrientation(a, b, point) === 0 &&
    point[0]! >= Math.min(a[0]!, b[0]!) - ROUTING_EPSILON &&
    point[0]! <= Math.max(a[0]!, b[0]!) + ROUTING_EPSILON &&
    point[1]! >= Math.min(a[1]!, b[1]!) - ROUTING_EPSILON &&
    point[1]! <= Math.max(a[1]!, b[1]!) + ROUTING_EPSILON
  );
}

/** Includes touching and collinear overlaps, which are unsafe for routing holes. */
function routingSegmentsMeet(
  a: RoutingPoint,
  b: RoutingPoint,
  c: RoutingPoint,
  d: RoutingPoint,
) {
  // Most edge pairs are far apart, especially for separate holes. Avoid the
  // orientation calculations until their bounds can meet.
  if (
    Math.max(a[0]!, b[0]!) + ROUTING_EPSILON < Math.min(c[0]!, d[0]!) ||
    Math.max(c[0]!, d[0]!) + ROUTING_EPSILON < Math.min(a[0]!, b[0]!) ||
    Math.max(a[1]!, b[1]!) + ROUTING_EPSILON < Math.min(c[1]!, d[1]!) ||
    Math.max(c[1]!, d[1]!) + ROUTING_EPSILON < Math.min(a[1]!, b[1]!)
  )
    return false;
  const abC = routingOrientation(a, b, c);
  const abD = routingOrientation(a, b, d);
  const cdA = routingOrientation(c, d, a);
  const cdB = routingOrientation(c, d, b);
  return (
    (abC * abD < 0 && cdA * cdB < 0) ||
    (abC === 0 && routingOnSegment(c, a, b)) ||
    (abD === 0 && routingOnSegment(d, a, b)) ||
    (cdA === 0 && routingOnSegment(a, c, d)) ||
    (cdB === 0 && routingOnSegment(b, c, d))
  );
}

function routingInsideRing(point: RoutingPoint, ring: number[][]) {
  let inside = false;
  for (let index = 0; index < ring.length; index++) {
    const a = ring[index]!;
    const b = ring[(index + 1) % ring.length]!;
    if (routingOnSegment(point, a, b)) return false;
    if (
      a[1]! > point[1]! !== b[1]! > point[1]! &&
      point[0]! <
        a[0]! + ((b[0]! - a[0]!) * (point[1]! - a[1]!)) / (b[1]! - a[1]!)
    )
      inside = !inside;
  }
  return inside;
}

function routingRingsMeet(a: number[][], b: number[][]) {
  const boundsA = architectureBounds(a);
  const boundsB = architectureBounds(b);
  if (
    boundsA.maxX + ROUTING_EPSILON < boundsB.minX ||
    boundsB.maxX + ROUTING_EPSILON < boundsA.minX ||
    boundsA.maxY + ROUTING_EPSILON < boundsB.minY ||
    boundsB.maxY + ROUTING_EPSILON < boundsA.minY
  )
    return false;
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (
        routingSegmentsMeet(
          a[i]!,
          a[(i + 1) % a.length]!,
          b[j]!,
          b[(j + 1) % b.length]!,
        )
      )
        return true;
    }
  }
  return false;
}

/** Explicit, bounded navigation geometry. Visual architecture remains independent. */
export function validateMapRouting(
  value: unknown,
  floor: MapGeometry["floor"],
): MapRouting {
  const path = "map.geometry.routing";
  const input = record(value, path, ["walkableAreas", "barriers", "doors"]);
  const areaIds = new Set<string>();
  const barrierIds = new Set<string>();
  const doorIds = new Set<string>();
  let vertices = 0;
  const point = (value: unknown, path: string): [number, number] => {
    const coordinates = array(value, path, 2, 2);
    return [
      number(coordinates[0], `${path}[0]`, floor.x, floor.x + floor.width),
      number(coordinates[1], `${path}[1]`, floor.y, floor.y + floor.height),
    ];
  };
  const walkableAreas = array(
    input.walkableAreas,
    `${path}.walkableAreas`,
    MAP_ROUTING_MAX_AREAS,
  ).map((value, areaIndex) => {
    const areaPath = `${path}.walkableAreas[${areaIndex}]`;
    const area = record(value, areaPath, ["id", "name", "polygon"]);
    const id = uniqueId(area.id, `${areaPath}.id`, areaIds);
    const name = text(area.name, `${areaPath}.name`, 160);
    const polygon = array(
      area.polygon,
      `${areaPath}.polygon`,
      MAP_ROUTING_MAX_RINGS,
      1,
    ).map((value, ringIndex) => {
      const ringPath = `${areaPath}.polygon[${ringIndex}]`;
      const ring = array(value, ringPath, MAP_ROUTING_MAX_RING_VERTICES, 3).map(
        (value, pointIndex) => {
          if (++vertices > MAP_ROUTING_MAX_VERTICES)
            invalid(
              path,
              `must not exceed ${MAP_ROUTING_MAX_VERTICES} vertices in total`,
            );
          return point(value, `${ringPath}[${pointIndex}]`);
        },
      );
      const used = new Set<string>();
      for (const [x, y] of ring) {
        const key = `${x},${y}`;
        if (used.has(key))
          invalid(
            ringPath,
            "must not repeat vertices, including the closing vertex",
          );
        used.add(key);
      }
      let twiceArea = 0;
      for (let i = 0; i < ring.length; i++) {
        const a = ring[i]!;
        const b = ring[(i + 1) % ring.length]!;
        const c = ring[(i + 2) % ring.length]!;
        if (Math.hypot(b[0] - a[0], b[1] - a[1]) <= ROUTING_EPSILON)
          invalid(ringPath, "must not contain zero-length edges");
        if (
          routingOrientation(a, b, c) === 0 &&
          (b[0] - a[0]) * (c[0] - b[0]) + (b[1] - a[1]) * (c[1] - b[1]) < 0
        )
          invalid(ringPath, "must not contain overlapping edges");
        twiceArea += routingCross(ring[0]!, a, b);
        for (let j = i + 1; j < ring.length; j++) {
          if (j === i + 1 || (i === 0 && j === ring.length - 1)) continue;
          if (routingSegmentsMeet(a, b, ring[j]!, ring[(j + 1) % ring.length]!))
            invalid(
              ringPath,
              "must be simple, without intersecting or touching edges",
            );
        }
      }
      if (Math.abs(twiceArea) <= ROUTING_EPSILON)
        invalid(ringPath, "must have nonzero area");
      return ring;
    });
    const outer = polygon[0]!;
    for (let i = 1; i < polygon.length; i++) {
      const hole = polygon[i]!;
      if (!routingInsideRing(hole[0]!, outer) || routingRingsMeet(hole, outer))
        invalid(
          `${areaPath}.polygon[${i}]`,
          "hole must stay strictly inside its outer ring",
        );
      for (let j = 1; j < i; j++) {
        const other = polygon[j]!;
        if (
          routingRingsMeet(hole, other) ||
          routingInsideRing(hole[0]!, other) ||
          routingInsideRing(other[0]!, hole)
        )
          invalid(
            `${areaPath}.polygon[${i}]`,
            "holes must not overlap, touch or contain each other",
          );
      }
    }
    return { id, name, polygon };
  });
  const barriers = array(
    input.barriers,
    `${path}.barriers`,
    MAP_ROUTING_MAX_BARRIERS,
  ).map((value, index) => {
    const barrierPath = `${path}.barriers[${index}]`;
    const barrier = record(value, barrierPath, ["id", "name", "start", "end"]);
    const id = uniqueId(barrier.id, `${barrierPath}.id`, barrierIds);
    const name = text(barrier.name, `${barrierPath}.name`, 160);
    const start = point(barrier.start, `${barrierPath}.start`);
    const end = point(barrier.end, `${barrierPath}.end`);
    if (Math.hypot(end[0] - start[0], end[1] - start[1]) <= ROUTING_EPSILON)
      invalid(barrierPath, "must have distinct start and end points");
    return { id, name, start, end };
  });
  const intervals = new Map<string, { start: number; end: number }[]>();
  const doors = array(input.doors, `${path}.doors`, MAP_ROUTING_MAX_DOORS).map(
    (value, index) => {
      const doorPath = `${path}.doors[${index}]`;
      const door = record(
        value,
        doorPath,
        ["id", "name", "barrierId", "start", "end"],
        ["closed"],
      );
      const id = uniqueId(door.id, `${doorPath}.id`, doorIds);
      const name = text(door.name, `${doorPath}.name`, 160);
      const barrierId = entityId(door.barrierId, `${doorPath}.barrierId`);
      if (!barrierIds.has(barrierId))
        invalid(`${doorPath}.barrierId`, "must refer to an existing barrier");
      const start = number(door.start, `${doorPath}.start`, 0, 1);
      const end = number(door.end, `${doorPath}.end`, 0, 1);
      if (start >= end)
        invalid(
          doorPath,
          "must have a start fraction smaller than its end fraction",
        );
      const otherIntervals = intervals.get(barrierId) ?? [];
      if (
        otherIntervals.some((other) => start < other.end && end > other.start)
      )
        invalid(doorPath, "must not overlap another door on the same barrier");
      otherIntervals.push({ start, end });
      intervals.set(barrierId, otherIntervals);
      return {
        id,
        name,
        barrierId,
        start,
        end,
        ...(Object.hasOwn(door, "closed")
          ? { closed: boolean(door.closed, `${doorPath}.closed`) }
          : {}),
      };
    },
  );
  return { walkableAreas, barriers, doors };
}

export function validateMapDocument(value: unknown): MapDocument {
  const source = record(value, "map", [
    "schemaVersion",
    "id",
    "title",
    "description",
    "geometry",
    "labels",
    "locations",
    "camera",
  ]);
  if (source.schemaVersion !== 1) invalid("map.schemaVersion", "must be 1");
  if (!validMapId(source.id))
    invalid("map.id", "must be a lowercase slug of at most 80 characters");
  const title = text(source.title, "map.title", 160, true);
  const description = text(
    source.description,
    "map.description",
    2_000,
    false,
    true,
  );
  const inputGeometry = record(
    source.geometry,
    "map.geometry",
    ["width", "height", "floor", "stands", "entrances"],
    ["architecture", "routing", "artwork", "artworkLabels"],
  );
  const width = number(
    inputGeometry.width,
    "map.geometry.width",
    0,
    MAX_COORDINATE,
    true,
  );
  const height = number(
    inputGeometry.height,
    "map.geometry.height",
    0,
    MAX_COORDINATE,
    true,
  );
  const inputFloor = record(
    inputGeometry.floor,
    "map.geometry.floor",
    ["x", "y", "width", "height", "radius"],
    ["color"],
  );
  const floor = {
    ...(Object.hasOwn(inputFloor, "color")
      ? { color: mapColor(inputFloor.color, "map.geometry.floor.color") }
      : {}),
    x: number(inputFloor.x, "map.geometry.floor.x", 0, width),
    y: number(inputFloor.y, "map.geometry.floor.y", 0, height),
    width: number(inputFloor.width, "map.geometry.floor.width", 0, width, true),
    height: number(
      inputFloor.height,
      "map.geometry.floor.height",
      0,
      height,
      true,
    ),
    radius: number(
      inputFloor.radius,
      "map.geometry.floor.radius",
      0,
      Math.min(width, height) / 2,
    ),
  };
  if (floor.x + floor.width > width || floor.y + floor.height > height)
    invalid("map.geometry.floor", "must fit within the map dimensions");
  if (floor.radius > Math.min(floor.width, floor.height) / 2)
    invalid(
      "map.geometry.floor.radius",
      "must not exceed half the floor dimensions",
    );
  const standIds = new Set<string>();
  let vertices = 0;
  const stands: MapStand[] = array(
    inputGeometry.stands,
    "map.geometry.stands",
    2_000,
  ).map((value, index) => {
    const path = `map.geometry.stands[${index}]`;
    const input = record(
      value,
      path,
      ["id", "name", "shortName", "category", "polygons", "label"],
      [
        "height",
        "hidden",
        "logo",
        "labelMode",
        "mapLabel",
        "labelRotation",
        "color",
        "borderColor",
      ],
    );
    const id = uniqueId(input.id, `${path}.id`, standIds);
    if (
      typeof input.category !== "string" ||
      !CATEGORIES.has(input.category as MapCategory)
    )
      invalid(`${path}.category`, "must be a supported stand category");
    const polygons = array(input.polygons, `${path}.polygons`, 16, 1).map(
      (value, polygonIndex) => {
        const polygonPath = `${path}.polygons[${polygonIndex}]`;
        const polygon = array(value, polygonPath, 128, 3).map(
          (value, pointIndex) => {
            if (++vertices > MAX_VERTICES)
              invalid(
                "map.geometry.stands",
                `must not exceed ${MAX_VERTICES} vertices in total`,
              );
            const pointPath = `${polygonPath}[${pointIndex}]`;
            const point = array(value, pointPath, 2, 2);
            return [
              number(point[0], `${pointPath}[0]`, 0, width),
              number(point[1], `${pointPath}[1]`, 0, height),
            ];
          },
        );
        // Translate to the first vertex before summing to avoid cancellation at large coordinates.
        const origin = polygon[0]!;
        let twiceArea = 0;
        for (let i = 0; i < polygon.length; i++) {
          const a = polygon[i]!;
          const b = polygon[(i + 1) % polygon.length]!;
          twiceArea +=
            (a[0]! - origin[0]!) * (b[1]! - origin[1]!) -
            (b[0]! - origin[0]!) * (a[1]! - origin[1]!);
        }
        if (Math.abs(twiceArea) <= 1e-8)
          invalid(polygonPath, "must have nonzero area");
        return polygon;
      },
    );
    const inputLabel = record(
      input.label,
      `${path}.label`,
      ["x", "y", "rotation", "idFontSize", "idY", "nameFontSize", "nameLines"],
      ["showId"],
    );
    if (
      inputLabel.showId !== undefined &&
      typeof inputLabel.showId !== "boolean"
    )
      invalid(`${path}.label.showId`, "must be a boolean");
    const label: MapStand["label"] = {
      ...(inputLabel.showId === undefined
        ? {}
        : { showId: inputLabel.showId as boolean }),
      x: number(inputLabel.x, `${path}.label.x`, 0, width),
      y: number(inputLabel.y, `${path}.label.y`, 0, height),
      rotation: number(
        inputLabel.rotation,
        `${path}.label.rotation`,
        -360,
        360,
      ),
      idFontSize: number(
        inputLabel.idFontSize,
        `${path}.label.idFontSize`,
        0,
        1_000,
        true,
      ),
      idY: number(inputLabel.idY, `${path}.label.idY`, -height, height),
      nameFontSize: number(
        inputLabel.nameFontSize,
        `${path}.label.nameFontSize`,
        0,
        1_000,
        true,
      ),
      nameLines: array(inputLabel.nameLines, `${path}.label.nameLines`, 20).map(
        (value, lineIndex) => {
          const linePath = `${path}.label.nameLines[${lineIndex}]`;
          const line = record(value, linePath, ["text", "y"]);
          return {
            text: text(line.text, `${linePath}.text`, 160),
            y: number(line.y, `${linePath}.y`, -height, height),
          };
        },
      ),
    };
    const mapLabel = Object.hasOwn(input, "mapLabel")
      ? text(input.mapLabel, `${path}.mapLabel`, 160).trim()
      : "";
    return {
      id,
      name: text(input.name, `${path}.name`, 300),
      shortName: text(input.shortName, `${path}.shortName`, 160),
      ...(mapLabel ? { mapLabel } : {}),
      ...(Object.hasOwn(input, "labelRotation")
        ? {
            labelRotation: labelRotation(
              input.labelRotation,
              `${path}.labelRotation`,
            ),
          }
        : {}),
      category: input.category as MapCategory,
      ...(Object.hasOwn(input, "color")
        ? { color: mapColor(input.color, `${path}.color`) }
        : {}),
      ...(Object.hasOwn(input, "borderColor")
        ? { borderColor: mapColor(input.borderColor, `${path}.borderColor`) }
        : {}),
      ...(Object.hasOwn(input, "logo")
        ? { logo: logo(input.logo, `${path}.logo`) }
        : {}),
      ...(Object.hasOwn(input, "labelMode")
        ? { labelMode: labelMode(input.labelMode, `${path}.labelMode`) }
        : {}),
      polygons,
      label,
      ...(Object.hasOwn(input, "height")
        ? { height: number(input.height, `${path}.height`, 0, 100) }
        : {}),
      ...(Object.hasOwn(input, "hidden")
        ? { hidden: boolean(input.hidden, `${path}.hidden`) }
        : {}),
    };
  });
  const entranceIds = new Set<string>();
  const entrances: MapGeometry["entrances"] = array(
    inputGeometry.entrances,
    "map.geometry.entrances",
    100,
  ).map((value, index) => {
    const path = `map.geometry.entrances[${index}]`;
    const input = record(value, path, ["id", "label", "x", "y"]);
    const id = uniqueId(input.id, `${path}.id`, entranceIds);
    if (standIds.has(id))
      invalid(`${path}.id`, "must not duplicate a stand identifier");
    return {
      id,
      label: text(input.label, `${path}.label`, 300, true),
      x: number(input.x, `${path}.x`, 0, width),
      y: number(input.y, `${path}.y`, 0, height),
    };
  });
  const locationIds = new Set<string>();
  const locations: MapLocation[] = array(
    source.locations,
    "map.locations",
    3_000,
  ).map((value, index) => {
    const path = `map.locations[${index}]`;
    const input = record(
      value,
      path,
      ["id", "label", "x", "y"],
      ["hidden", "preserveOnRefresh"],
    );
    return {
      id: uniqueId(input.id, `${path}.id`, locationIds),
      label: text(input.label, `${path}.label`, 300, true),
      x: number(input.x, `${path}.x`, 0, 1),
      y: number(input.y, `${path}.y`, 0, 1),
      ...(Object.hasOwn(input, "preserveOnRefresh")
        ? {
            preserveOnRefresh: boolean(
              input.preserveOnRefresh,
              `${path}.preserveOnRefresh`,
            ),
          }
        : {}),
      ...(Object.hasOwn(input, "hidden")
        ? { hidden: boolean(input.hidden, `${path}.hidden`) }
        : {}),
    };
  });
  const inputCamera = record(source.camera, "map.camera", [
    "rotation",
    "pitch",
  ]);
  const floorPaintBudget = { comparisons: 0 };
  const document: MapDocument = {
    schemaVersion: 1,
    id: source.id,
    title,
    description,
    geometry: {
      width,
      height,
      floor,
      stands,
      entrances,
      ...(Object.hasOwn(inputGeometry, "routing")
        ? { routing: validateMapRouting(inputGeometry.routing, floor) }
        : {}),
      ...(Object.hasOwn(inputGeometry, "architecture")
        ? {
            architecture: architecture(
              inputGeometry.architecture,
              floor,
              floorPaintBudget,
            ),
          }
        : {}),
      ...(Object.hasOwn(inputGeometry, "artwork")
        ? { artwork: artwork(inputGeometry.artwork, floor, floorPaintBudget) }
        : {}),
      ...(Object.hasOwn(inputGeometry, "artworkLabels")
        ? { artworkLabels: artworkLabels(inputGeometry.artworkLabels, floor) }
        : {}),
    },
    labels: labels(source.labels, "map.labels", standIds, entranceIds),
    locations,
    camera: {
      rotation: number(inputCamera.rotation, "map.camera.rotation", -360, 360),
      pitch: number(inputCamera.pitch, "map.camera.pitch", 0, 70),
    },
  };
  if (
    new TextEncoder().encode(JSON.stringify(document)).byteLength >
    MAP_DOCUMENT_MAX_BYTES
  )
    invalid("map", "exceeds the 1 MiB document limit");
  return document;
}
